import React, { useRef, useCallback, useEffect } from 'react'
import { EventChainItemInput } from '@app/services/eventChain/types'
import { logEventChain } from '@app/services/eventChain/mutations'
import { initEventChainDB, EventChainDBActions } from '@app/helpers/indexedDB/eventChainDB'

type EventChainMode = 'online' | 'offline'

interface EventChainState {
  scopeKey: string
  tailId: string | null
  spliceId: string | null
  buffer: EventChainItemInput[]
  isFlushing: boolean
  debounceTimer: ReturnType<typeof setTimeout> | null
  mode: EventChainMode
  // Not persisted to IDB — resets on reload. After a reload, the first event's
  // duration will reflect total elapsed time rather than time-on-question.
  lastPassedTime: number
  pendingWrites: Promise<void>
  db: EventChainDBActions | null
}

const UNKNOWN_SCOPE_KEY = 'unknown'
const GROUP_SCOPE_PARAMS = [
  'contentId',
  'classId',
  'teacherId',
  'schoolId',
  'districtId',
  'organizationId',
  'preview',
  'questionPreviewId',
]

const scopePart = (value: string | null): string =>
  encodeURIComponent(value || 'none')

const getEventChainScopeKey = (urlParams: URLSearchParams): string => {
  const assessmentId = scopePart(urlParams.get('id'))
  const schoolYear = scopePart(urlParams.get('schoolYear'))
  const attempt = scopePart(urlParams.get('attempt'))
  const userId = urlParams.get('userId')

  if (userId) {
    return ['student', scopePart(userId), assessmentId, schoolYear, attempt].join(':')
  }

  const groupScope = GROUP_SCOPE_PARAMS
    .map(param => `${param}=${scopePart(urlParams.get(param))}`)
    .join('&')

  return ['group', assessmentId, schoolYear, attempt, groupScope].join(':')
}

// Module-level singleton so non-React code can call these
export const eventChain = {
  trackEvent: (_event: EventChainItemInput, _passedTime?: number): void => undefined,
  resetChain: (): Promise<void> => Promise.resolve(),
  getTailId: (): string | null => null,
  flush: (): Promise<void> => Promise.resolve(),
}

export const EventChainProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const stateRef = useRef<EventChainState>({
    scopeKey: UNKNOWN_SCOPE_KEY,
    tailId: null,
    spliceId: null,
    buffer: [],
    isFlushing: false,
    debounceTimer: null,
    mode: 'online',
    lastPassedTime: 0,
    pendingWrites: Promise.resolve(),
    db: null,
  })

  const scheduleFlushRef = useRef<() => void>(() => undefined)

  const goOffline = useCallback(async (failedBatch?: EventChainItemInput[]) => {
    const state = stateRef.current
    const scopeKey = state.scopeKey

    // Save current tailId as spliceId only on the first offline transition.
    // Repeated transitions must not clobber the original fork point.
    if (state.spliceId === null) {
      state.spliceId = state.tailId
    }
    state.mode = 'offline'

    if (state.db) {
      await state.db.setMeta(scopeKey, 'spliceId', state.spliceId)

      // Move failed batch to IndexedDB
      if (failedBatch && failedBatch.length > 0) {
        await state.db.addEvents(scopeKey, failedBatch)
      }

      // Move any remaining in-memory buffer to IndexedDB
      if (state.buffer.length > 0) {
        await state.db.addEvents(scopeKey, state.buffer)
        state.buffer = []
      }
    }
  }, [])

  const drainScope = useCallback(async (scopeKey: string): Promise<boolean> => {
    const state = stateRef.current
    if (!state.db) return true

    // Ensure any chained offline writes have committed before reading IDB
    if (scopeKey === state.scopeKey) {
      await state.pendingWrites
    }

    const drained = await state.db.getAllEventsWithKeys(scopeKey)
    const isCurrentScope = scopeKey === state.scopeKey
    if (drained.length === 0) {
      if (isCurrentScope) {
        state.mode = 'online'
        state.spliceId = null
      }
      await state.db.setMeta(scopeKey, 'spliceId', null)
      return true
    }

    const events = drained.map(d => d.value)
    const spliceId = isCurrentScope
      ? state.spliceId
      : await state.db.getMeta(scopeKey, 'spliceId')
    const previousTailId = isCurrentScope
      ? state.tailId
      : await state.db.getMeta(scopeKey, 'tailId')

    try {
      const input = spliceId
        ? { spliceId, events }
        : previousTailId
          ? { tailId: previousTailId, events }
          : { events }

      const result = await logEventChain(input)
      const tailId = result?.lastEventId ?? previousTailId

      if (isCurrentScope) {
        state.tailId = tailId ?? state.tailId
        state.spliceId = null
        state.mode = 'online'
      }

      // Only delete the keys we captured — events that arrived during the
      // await window are preserved in IDB for the next flush.
      await state.db.deleteByKeys(drained.map(d => d.key))
      await state.db.setMeta(scopeKey, 'tailId', tailId)
      await state.db.setMeta(scopeKey, 'spliceId', null)
      return true
    } catch {
      if (isCurrentScope) {
        state.mode = 'offline'
      }
      return false
    }
  }, [])

  const goOnline = useCallback(async (): Promise<boolean> => {
    const state = stateRef.current
    return drainScope(state.scopeKey)
  }, [drainScope])

  const flush = useCallback(async () => {
    const state = stateRef.current
    if (state.isFlushing || state.buffer.length === 0) return

    state.isFlushing = true
    const batch = state.buffer.splice(0, state.buffer.length)

    try {
      const input = state.tailId
        ? { tailId: state.tailId, events: batch }
        : { events: batch }
      const result = await logEventChain(input)
      state.tailId = result?.lastEventId ?? state.tailId

      if (state.db) {
        await state.db.setMeta(state.scopeKey, 'tailId', state.tailId)
      }
    } catch (err: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[EventChain] flush error', err)
      }
      const isNetworkError = !!err?.networkError || !navigator.onLine

      if (isNetworkError) {
        await goOffline(batch)
      }
      // Non-network errors: drop batch, preserve tailId (same as Ready)
    } finally {
      state.isFlushing = false
      if (state.buffer.length > 0 && state.mode === 'online') {
        scheduleFlushRef.current()
      }
    }
  }, [goOffline])

  const scheduleFlush = useCallback(() => {
    const state = stateRef.current
    if (state.debounceTimer !== null) {
      clearTimeout(state.debounceTimer)
    }
    state.debounceTimer = setTimeout(() => {
      state.debounceTimer = null
      if (!state.isFlushing) {
        flush()
      }
    }, 500)
  }, [flush])

  useEffect(() => {
    scheduleFlushRef.current = scheduleFlush
  }, [scheduleFlush])

  const trackEvent = useCallback(
    (event: EventChainItemInput, passedTime?: number) => {
      const state = stateRef.current
      const currentPassedTime = passedTime ?? 0
      const duration = Math.max(
        Math.round((currentPassedTime - state.lastPassedTime) * 1000),
        0
      )
      state.lastPassedTime = currentPassedTime

      const eventWithDuration: EventChainItemInput = { ...event, duration }

      if (state.mode === 'online') {
        state.buffer.push(eventWithDuration)
        scheduleFlush()
      } else {
        // Offline: write directly to IndexedDB, chained so flush() can await completion
        if (state.db) {
          const scopeKey = state.scopeKey
          state.pendingWrites = state.pendingWrites.then(
            () => state.db!.addEvent(scopeKey, eventWithDuration)
          )
        }
      }
    },
    [scheduleFlush]
  )

  const resetChain = useCallback(async () => {
    const state = stateRef.current
    if (state.debounceTimer !== null) {
      clearTimeout(state.debounceTimer)
      state.debounceTimer = null
    }
    state.tailId = null
    state.spliceId = null
    state.buffer = []
    state.isFlushing = false
    state.mode = 'online'
    state.lastPassedTime = 0

    if (state.db) {
      await state.db.clearBuffer(state.scopeKey)
      await state.db.clearMeta(state.scopeKey)
    }
  }, [])

  // Initialize: open IndexedDB, load persisted state, flush pending events
  useEffect(() => {
    let isMounted = true

    const init = async () => {
      // Read tailId from URL (passed by Ready when launching QTI)
      const urlParams = new URLSearchParams(window.location.search)
      const urlTailId = urlParams.get('eventChainTailId')
      const scopeKey = getEventChainScopeKey(urlParams)

      if (urlTailId) {
        urlParams.delete('eventChainTailId')
        const remaining = urlParams.toString()
        const newUrl = `${window.location.pathname}${remaining ? '?' + remaining : ''}${window.location.hash}`
        window.history.replaceState({}, '', newUrl)
      }

      const db = await initEventChainDB()
      if (!isMounted) return

      const state = stateRef.current
      state.db = db
      state.scopeKey = scopeKey

      const tailId = await db.getMeta(scopeKey, 'tailId')
      const spliceId = await db.getMeta(scopeKey, 'spliceId')
      state.tailId = tailId
      state.spliceId = spliceId

      // Check for pending offline events and flush immediately if online
      const currentPendingCount = await db.getBufferCount(scopeKey)
      if (currentPendingCount > 0 && navigator.onLine) {
        const drained = await goOnline()
        if (!drained) return
      } else if (currentPendingCount > 0) {
        state.mode = 'offline'
        return
      }

      if (urlTailId) {
        state.tailId = urlTailId
        state.spliceId = null
        state.mode = 'online'
        await db.setMeta(scopeKey, 'tailId', urlTailId)
        await db.setMeta(scopeKey, 'spliceId', null)
      }

    }

    init()

    const handleOffline = () => {
      goOffline()
    }

    const handleOnline = () => {
      goOnline()
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    const cleanupState = stateRef.current

    return () => {
      isMounted = false
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)

      if (cleanupState.debounceTimer !== null) {
        clearTimeout(cleanupState.debounceTimer)
      }
    }
  }, [goOffline, goOnline, drainScope])

  // Expose via module-level singleton for use outside React
  React.useLayoutEffect(() => {
    eventChain.trackEvent = trackEvent
    eventChain.resetChain = resetChain
    eventChain.getTailId = () => stateRef.current.tailId
    eventChain.flush = async () => {
      const state = stateRef.current
      if (state.debounceTimer !== null) {
        clearTimeout(state.debounceTimer)
        state.debounceTimer = null
      }
      await state.pendingWrites
      await flush()
    }
    return () => {
      eventChain.trackEvent = (_event: EventChainItemInput, _passedTime?: number): void => undefined
      eventChain.resetChain = (): Promise<void> => Promise.resolve()
      eventChain.getTailId = (): string | null => null
      eventChain.flush = (): Promise<void> => Promise.resolve()
    }
  }, [trackEvent, resetChain, flush])

  return <>{children}</>
}
